/**
 * Invite-accept landing page (pirx-z0v4g.7).
 *
 * Reads the invite token from the URL (`/teams/accept?token=...`) and POSTs it
 * to the backend accept route. Requires an authenticated session whose email
 * matches the invite. Renders distinct, non-crashing states for success, an
 * expired/already-used invite, an invalid/unknown token, a wrong-account
 * (email mismatch) error, and the signed-out case.
 *
 * The backend is the authority: it validates the token, expiry, and email match
 * and creates the membership. This page only classifies the outcome for display.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/useAuth";
import {
  type AcceptInviteResult,
  acceptTeamInvite,
} from "@/services/api-client";
import { ApiError } from "@/services/api/errors";

type AcceptStatus = "loading" | "success" | "invalid" | "expired"
  | "wrongAccount" | "error";
type Status = AcceptStatus | "noToken" | "signedOut";

function classifyError(err: unknown): AcceptStatus {
  if (err instanceof ApiError) {
    if (err.status === 404) return "invalid";
    // Real expiry arrives as 400 (the backend's ValidationError "This
    // invite has expired"), not 409.
    if (err.status === 400 && /expire/i.test(err.message)) return "expired";
    // 409 = already used / already a member; the "expired" copy covers
    // both.
    if (err.status === 409) return "expired";
    if (err.status === 403) return "wrongAccount";
  }
  return "error";
}

export default function InviteAccept() {
  const { t } = useTranslation(["settings", "common"]);
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // Result of the async accept; only meaningful once we actually attempt it.
  const [acceptStatus, setAcceptStatus] = useState<AcceptStatus>("loading");
  const [result, setResult] = useState<AcceptInviteResult | null>(null);
  const attempted = useRef(false);

  const canAttempt = !authLoading && Boolean(token) && Boolean(user);
  const attemptAccept = useCallback(() => {
    if (!token) return;
    setAcceptStatus("loading");
    void (async () => {
      try {
        setResult(await acceptTeamInvite(token));
        setAcceptStatus("success");
      } catch (err) {
        setAcceptStatus(classifyError(err));
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!canAttempt || !token) return;
    // Accept exactly once (guard against StrictMode double-invoke).
    if (attempted.current) return;
    attempted.current = true;
    attemptAccept();
  }, [canAttempt, token, attemptAccept]);

  // Pre-accept states are derived during render (no token / signed out); once we
  // can attempt, the async accept status drives the display.
  const status: Status = authLoading
    ? "loading"
    : !token
    ? "noToken"
    : !user
    ? "signedOut"
    : acceptStatus;

  const messageKey: Record<Status, string> = {
    loading: "settings:teams.accept.loading",
    success: "settings:teams.accept.success",
    invalid: "settings:teams.accept.invalid",
    expired: "settings:teams.accept.expired",
    wrongAccount: "settings:teams.accept.wrongAccount",
    error: "settings:teams.accept.error",
    noToken: "settings:teams.accept.noToken",
    signedOut: "settings:teams.accept.signedOut",
  };

  return (
    <div
      className="mx-auto flex min-h-[60dvh] max-w-lg flex-col items-center justify-center gap-4 px-4 text-center"
      data-test="invite-accept"
    >
      <h1 className="text-2xl font-extrabold tracking-tight text-[var(--foreground)]">
        {t("settings:teams.accept.title")}
      </h1>

      <p
        className="text-[var(--muted-foreground)]"
        data-test={`invite-accept-${status}`}
      >
        {status === "success"
          ? t("settings:teams.accept.success", {
            team: result?.teamName ?? "",
          })
          : t(messageKey[status])}
      </p>

      {status === "success" && (
        <Button
          variant="cta"
          render={<Link to="/" />}
          data-test="invite-accept-continue"
        >
          {t("settings:teams.accept.continue")}
        </Button>
      )}

      {status === "signedOut" && (
        <Button
          variant="cta"
          render={
            <Link
              to="/login"
              state={{ next: location.pathname + location.search }}
            />
          }
          data-test="invite-accept-signin"
        >
          {t("common:nav.signIn")}
        </Button>
      )}

      {status === "error" && (
        <Button
          variant="outline"
          onClick={() => {
            attempted.current = false;
            attemptAccept();
          }}
          data-test="invite-accept-retry"
        >
          {t("common:buttons.retry")}
        </Button>
      )}

      {(status === "invalid" || status === "expired" || status === "noToken") && (
        <Button
          variant="secondary"
          render={<Link to="/" />}
          data-test="invite-accept-open-app"
        >
          {t("settings:teams.accept.openApp")}
        </Button>
      )}
    </div>
  );
}
